import React, {useState, useEffect} from "react";
import {useFormik} from "formik";
import {useHistory} from "react-router-dom";
import {useObserver} from "mobx-react-lite";
import moment from "moment";
import {
    Row,
    Col,
    Form,
    Input,
    Select,
    Dropdown,
    Menu,
    Modal,
    Switch,
    Checkbox,
    Tooltip
} from "antd";
import {EllipsisOutlined, HomeOutlined, AimOutlined} from "@ant-design/icons";
import MaskedInput from "antd-mask-input";
import {useTranslation} from "react-i18next";

import placeholder from "../../../public/images/person-placeholder.jpg";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {ImageEditorModal} from "../../_defaultComponents/ImageEditorModal/ImageEditorModal";
import {newCourierValidation} from "./newCourierValidation";
import {newCourierFormPropType, newCourierPropType} from "./newCourierPropTypes";
import {ImgBg, IconContainer, Img, DatePickerField, GeolocateButton} from "./newCourierStyles";
import {courierTypes, cities, counties, bloodTypes} from "../../../constants/constants";
import {arrayAlphabeticalSort} from "../../../utils/helpers";
import {ConditionalTooltip} from "../../_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {SingleMarkerMap} from "../../_defaultComponents/SingleMarkerMap/SingleMarkerMap";
import locale from "antd/es/date-picker/locale/tr_TR";
import {UpperGroupMultipleSelect} from "../../_defaultComponents/UpperGroupMultipleSelect/UpperGroupMultipleSelect";

export const NewCourier = ({courierId}: newCourierPropType) => {
    const stores = useStore();
    const history = useHistory();
    const {t, i18n} = useTranslation(["newCourier"]);
    const [editorVisible, setEditorVisible] = useState(false);
    const [showImage, setShowImage] = useState(false);
    const [courierPhoto, setCourierPhoto] = useState("");
    const [criminalCheck, setCriminalCheck] = useState(!!courierId);
    const [upperGroups, setUpperGroups] = useState([]);
    const [geolocationLoading, setGeolocationLoading] = useState(false);
    const [loadingCourier, courier = {}, setCourier, setLoadingCourier] = useFetch({
        fetchFn: stores.courierStore.getCourier.bind(null, {courierId}),
        showLoading: true,
        condition: !!courierId
    });
    const formik = useFormik({
        initialValues: {
            fullname: "",
            type: "",
            cellPhone: "",
            email: "",
            birthDate: "",
            tckn: "",
            bloodGroup: "",
            address: "",
            vehicleInfo: "",
            emergencyContactName: "",
            emergencyContactPhone: "",
            emergencyContactDegree: "",
            city: "",
            county: "",
            bankName: "",
            iban: "",
            taxNumber: "",
            trained: false,
            isTestUser: false,
            photo: "",
            gender: "",
            homeLocation: {
                longitude: 0,
                latitude: 0
            },
            upperGroups: [],
            ...(courier && courier.data)
        },
        validationSchema: newCourierValidation,
        enableReinitialize: true,
        onSubmit: async (values: newCourierFormPropType) => {
            await setLoadingCourier(true);
            values.birthDate = moment(values.birthDate).format("YYYY-MM-DD");
            values.cellPhone = values.cellPhone.replace(/[-,(,)]/g, "");
            values.emergencyContactPhone = values.emergencyContactPhone.replace(/[-,(,)]/g, "");
            values.upperGroups = upperGroups;
            if (courierPhoto) {
                await courierPhoto.toBlob(async blob => {
                    const resultOfRequest = await stores.courierStore.uploadImage(blob);
                    values.photo = resultOfRequest.imageId;
                    await handleCourierSubmit(values);
                });
            } else {
                if (values.photoId) {
                    values.photo = values.photoId;
                }
                await handleCourierSubmit(values);
            }
        }
    });

    const handleCourierSubmit = async (values: newCourierFormPropType) => {
        if (courierId) {
            const result = await stores.courierStore.updateCourier({courier: values});
            await setLoadingCourier(false);
            if (result) {
                history.push("/couriers/" + courierId);
            }
        } else {
            values.status = "unavailable";
            const result = await stores.courierStore.createCourier(values);
            await setLoadingCourier(false);
            if (result) {
                history.push("/couriers/" + result._id);
            }
        }
    };

    const geolocateCourierHomeLocation = async e => {
        setGeolocationLoading(true);

        const result = await stores.courierStore.getCourierHomeLocation({
            address: formik.values.address
        });

        if (result) {
            const coords = {
                longitude: result?.lon || 0,
                latitude: result?.lat || 0
            };
            formik.setFieldValue("homeLocation", coords);
        }
        setGeolocationLoading(false);
    };

    const renderFormInput = (textKey: string, fieldKey: string, span = 11, required = false) => (
        <Col span={span}>
            <Form.Item
                label={
                    i18n?.exists(`fields.${textKey}`, {ns: "common"})
                        ? t(`fields.${textKey}`, {ns: "common"})
                        : textKey
                }
                required={required}
                data-testid={`${fieldKey}Input`}
                validateStatus={errors[fieldKey] && touched[fieldKey] ? "error" : "success"}
                help={errors[fieldKey] && touched[fieldKey] ? errors[fieldKey] : null}
            >
                <Input name={fieldKey} value={values[fieldKey]} onChange={handleChange} />
            </Form.Item>
        </Col>
    );

    const renderFormSelect = (
        textKey: string,
        fieldKey: string,
        data: any[],
        mapKey = null,
        mapValue = null,
        disabled,
        span: number = 11,
        textFromi18n: boolean = false,
        required = false
    ) => (
        <Col span={span}>
            <Form.Item
                label={t(`fields.${textKey}`, {ns: "common"})}
                required={required}
                validateStatus={errors[fieldKey] && touched[fieldKey] ? "error" : "success"}
                help={errors[fieldKey] && touched[fieldKey] ? errors[fieldKey] : null}
            >
                <Select
                    allowClear
                    showSearch
                    name={fieldKey}
                    disabled={disabled}
                    value={values[fieldKey]}
                    data-testid={`${fieldKey}Select`}
                    onChange={value => setFieldValue(fieldKey, value)}
                >
                    {data?.map(item => (
                        <Select.Option
                            key={mapKey ? item[mapKey] : item}
                            value={mapKey ? item[mapKey] : item}
                            data-testid="courierTypeSelect_option"
                        >
                            {mapValue
                                ? item[mapValue]
                                : textFromi18n
                                ? t(`fields.${item}`, {ns: "common"})
                                : item}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
        </Col>
    );

    const renderMaskedInput = (textKey: string, fieldKey: string, span = 11, required = false) => (
        <Col span={span}>
            <Form.Item
                label={t(`fields.${textKey}`, {ns: "common"})}
                required={required}
                validateStatus={errors[fieldKey] && touched[fieldKey] ? "error" : "success"}
                help={errors[fieldKey] && touched[fieldKey] ? errors[fieldKey] : null}
            >
                <MaskedInput
                    name={fieldKey}
                    placeholder="0(555)-555-5555"
                    mask="1(111)-111-1111"
                    value={values[fieldKey]}
                    onChange={handleChange}
                    data-testid={`${fieldKey}Number`}
                />
            </Form.Item>
        </Col>
    );

    const renderTextArea = (
        textKey: string,
        fieldKey: string,
        placeholder: string,
        required = true
    ) => (
        <Form.Item
            label={t(`fields.${textKey}`, {ns: "common"})}
            validateStatus={errors[fieldKey] && touched[fieldKey] ? "error" : "success"}
            required={required}
            help={errors[fieldKey] && touched[fieldKey] ? errors[fieldKey] : null}
        >
            <Input.TextArea
                name={fieldKey}
                placeholder={placeholder}
                value={values[fieldKey]}
                onChange={handleChange}
                data-testid={`${fieldKey}Input`}
            />
        </Form.Item>
    );

    const renderCheckbox = (value: Boolean, setValue: () => void, textKey: string) => (
        <Col>
            <Checkbox
                checked={value}
                data-testid={`${textKey}Checkbox`}
                data-testvalue={value}
                onChange={e => setValue(e.target.checked)}
            >
                {t(textKey, {ns: "newCourier"})}
            </Checkbox>
        </Col>
    );

    const renderDatePicketField = (textKey: string, fieldKey: string, required = false) => (
        <Col span={11}>
            <Form.Item
                label={t(`fields.${textKey}`, {ns: "common"})}
                required={required}
                validateStatus={errors[fieldKey] && touched[fieldKey] ? "error" : "success"}
                help={errors[fieldKey] && touched[fieldKey] ? errors[fieldKey] : null}
            >
                <DatePickerField
                    locale={locale}
                    value={values[fieldKey] ? moment(values[fieldKey]) : ""}
                    name="birthDate"
                    format="DD/MM/YYYY"
                    data-testid={`${fieldKey}Picker`}
                    onChange={date => setFieldValue("birthDate", date)}
                />
            </Form.Item>
        </Col>
    );

    const renderSwitch = (textKey: string, fieldKey: string, span: number = 11) => {
        return (
            <Col span={span}>
                <Form.Item label={t(`fields.${textKey}`, {ns: "common"})}>
                    <Switch
                        checked={values[fieldKey]}
                        onChange={checked => setFieldValue(fieldKey, checked)}
                        data-testid={`${fieldKey}Switch`}
                    />
                </Form.Item>
            </Col>
        );
    };

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        if (courier.data) {
            setFieldValue("upperGroups", courier.data?.upperGroups);
            setUpperGroups(courier.data.upperGroups);
        }
    }, [courier]);

    return useObserver(() => (
        <Row type="flex" justify="center" data-testid="newCourierScreen">
            <ImageEditorModal
                visible={editorVisible}
                setVisible={value => setEditorVisible(value)}
                setStatePhoto={img => setCourierPhoto(img)}
                setPhoto={photoId => setFieldValue("photo", photoId)}
            />
            <Modal
                visible={showImage}
                onCancel={() => setShowImage(false)}
                footer={null}
                data-testid="profilePhotoModal"
            >
                <Row type="flex" justify="center">
                    <Img src={values.photo || placeholder} data-testid="profilePhoto" />
                </Row>
            </Modal>
            <Col span={20} data-testid="newCourierFormCard">
                <Form
                    autoComplete="off"
                    labelCol={{span: 24}}
                    wrapperCol={{span: 24}}
                    onFinish={handleSubmit}
                    data-testid="newCourierForm"
                >
                    <ErrorFocus formik={formik} />
                    <Row type="flex" justify="center">
                        <ImgBg
                            isEdit={!!courierId}
                            onClick={courierId ? () => {} : () => setEditorVisible(true)}
                            src={values.photo || placeholder}
                            data-testid="profilePhoto"
                        >
                            {courierId && (
                                <Dropdown
                                    overlay={
                                        <Menu>
                                            {values.photo && (
                                                <Menu.Item
                                                    onClick={() => setShowImage(true)}
                                                    data-testid="profilePhoto_open"
                                                >
                                                    {t("actions.open", {ns: "common"})}
                                                </Menu.Item>
                                            )}
                                            <Menu.Item
                                                onClick={() => setEditorVisible(true)}
                                                data-testid="profilePhoto_update"
                                            >
                                                {t("actions.update", {ns: "common"})}
                                            </Menu.Item>
                                        </Menu>
                                    }
                                >
                                    <IconContainer>
                                        <EllipsisOutlined />
                                    </IconContainer>
                                </Dropdown>
                            )}
                            {!values.photo && !courierId && (
                                <CustomButton data-testid="profilePhoto_add">
                                    {t("actions.addImage", {ns: "common"})}
                                </CustomButton>
                            )}
                        </ImgBg>
                    </Row>

                    <Row type="flex" justify="space-between">
                        {renderFormInput("courierName", "fullname", 11, true)}
                        {renderFormSelect(
                            "courierType",
                            "type",
                            courierTypes,
                            "value",
                            "name",
                            false,
                            11,
                            true,
                            true
                        )}
                    </Row>
                    <Row type="flex" justify="space-between">
                        {renderSwitch("drivingTraining", "trained", 1)}
                        {renderSwitch("testUser", "isTestUser", 1)}
                        {renderFormSelect(
                            "gender",
                            "gender",
                            ["male", "female"],
                            null,
                            null,
                            false,
                            5,
                            true,
                            true
                        )}
                        {renderFormSelect("bloodGroup", "bloodGroup", bloodTypes)}
                    </Row>

                    <Row type="flex" justify="space-between">
                        {renderFormInput("identificationNumber", "tckn", 11, true)}
                        {renderDatePicketField("birthDate", "birthDate", true)}
                    </Row>
                    <Row type="flex" justify="space-between">
                        {renderMaskedInput("phone", "cellPhone", 11, true)}
                        {renderFormInput("email", "email", 11, true)}
                    </Row>

                    <Row type="flex" justify="space-between">
                        {renderFormSelect(
                            "city",
                            "city",
                            arrayAlphabeticalSort(cities.filter(item => item.value !== "all")),
                            "value",
                            "name",
                            false,
                            11,
                            true,
                            true
                        )}
                        {renderFormSelect(
                            "county",
                            "county",
                            arrayAlphabeticalSort(counties[values.city]),
                            "value",
                            "name",
                            !values.city,
                            11,
                            true,
                            true
                        )}
                    </Row>
                    <Row type="flex" justify="space-between">
                        <UpperGroupMultipleSelect
                            cities={values.city != "" ? values.city : ""}
                            upperGroups={upperGroups}
                            setUpperGroups={setUpperGroups}
                        ></UpperGroupMultipleSelect>
                    </Row>
                    {renderTextArea("address", "address", "", true)}
                    <Row
                        type="flex"
                        justify="space-between"
                        style={{marginTop: "24px", marginBottom: "24px"}}
                    >
                        <Col>{t("homeLocation", {ns: "newCourier"})}</Col>
                        <Col>
                            {`${t("fields.latitude", {
                                ns: "common"
                            })}: ${values.homeLocation?.latitude?.toFixed(5) || 0}`}
                            <span style={{marginLeft: "1em"}}>
                                {`${t("fields.longitude", {
                                    ns: "common"
                                })}: ${values.homeLocation?.longitude?.toFixed(5) || 0}`}
                            </span>
                        </Col>
                    </Row>

                    <SingleMarkerMap
                        onClick={({lat, lng}) => {
                            const c = {
                                longitude: lng,
                                latitude: lat
                            };
                            setFieldValue(`homeLocation`, c);
                        }}
                        center={{
                            lat: values?.homeLocation?.latitude || 0,
                            lng: values?.homeLocation?.longitude || 0
                        }}
                        icon={<HomeOutlined />}
                        customButton={
                            <Tooltip
                                title={t("actions.geolocateCourierHomeLocation", {
                                    ns: "newCourier"
                                })}
                            >
                                <GeolocateButton
                                    loading={geolocationLoading}
                                    icon={<AimOutlined />}
                                    size="large"
                                    onClick={async e => await geolocateCourierHomeLocation(e)}
                                />
                            </Tooltip>
                        }
                    />
                    {renderTextArea(
                        "vehicleInfo",
                        "vehicleInfo",
                        t("vehicleInfoPlaceholder", {ns: "newCourier"}),
                        true
                    )}

                    <Row type="flex" justify="space-between">
                        {renderFormInput("emergencyContactName", "emergencyContactName", 7, true)}
                        {renderMaskedInput(
                            "emergencyContactPhone",
                            "emergencyContactPhone",
                            7,
                            true
                        )}
                        {renderFormInput(
                            "emergencyContactDegree",
                            "emergencyContactDegree",
                            7,
                            true
                        )}
                    </Row>

                    <Row type="flex" justify="space-between">
                        {renderFormInput("bankName", "bankName", 11, true)}
                        {renderFormInput("iban", "iban", 11, true)}
                    </Row>
                    <Row type="flex" justify="space-between">
                        {renderFormInput("taxNumber", "taxNumber", 11, false)}
                        {renderFormInput("taxOffice", "taxOffice", 11, false)}
                    </Row>
                    <Row type="flex" justify="space-between">
                        {renderFormInput("Şahıs Şirketi Adı", "personCompany", 11, false)}
                    </Row>

                    <Row type="flex" justify="space-between">
                        {renderCheckbox(criminalCheck, setCriminalCheck, "criminalCheckText")}

                        <Col>
                            <ConditionalTooltip
                                text={t("criminalCheckTooltip", {ns: "newCourier"})}
                                condition={!criminalCheck}
                                child={
                                    <CustomButton
                                        type="primary"
                                        htmlType="submit"
                                        data-testid="submitButton"
                                        disabled={isSubmitting}
                                    >
                                        {t("actions.save", {ns: "common"})}
                                    </CustomButton>
                                }
                            />
                        </Col>
                    </Row>
                </Form>
            </Col>
        </Row>
    ));
};
